import { useMemo } from "react";
import { TradeIdsToPnlMapping } from "@shared/services/socket/types";
import { useSelector } from "react-redux";
import { SocketTopics, TradeData } from "@shared/types";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import { toFixed } from "@shared/utils/numbers";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import useSocket from "@shared/features/socket/useSocket";
import { skipToken } from "@reduxjs/toolkit/query";

export const useTradeSocketPnl = (
  topic: SocketTopics.PNL | SocketTopics.GLOBAL_PNL,
  tradeId?: number
) => {
  const socketPnlArgs = useMemo(
    () => (tradeId ? { tradeIds: [tradeId] } : skipToken),
    [tradeId]
  );
  const socketData = useSocket<typeof topic>(topic, socketPnlArgs);

  return tradeId ? socketData?.value?.[tradeId] : undefined;
};

export const useTotalPnl = (
  openTradesData: TradeData[],
  socketPnlValue?: TradeIdsToPnlMapping
) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();

  return useMemo(() => {
    const pnlKey =
      displayCurrency === DISPLAY_CURRENCIES.USD
        ? "usd_amount_change"
        : "btc_amount_change";
    return toFixed(
      openTradesData.reduce((sum, trade) => {
        const tradePnlData = socketPnlValue?.[trade.id] ?? trade;
        return sum + tradePnlData[pnlKey];
      }, 0),
      precision
    );
  }, [displayCurrency, openTradesData, socketPnlValue]);
};
